import { Suspense, type ReactNode } from 'react';
import { useLocation, useParams, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Box, useModals } from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { IdentifierType } from '@/features/workspaces';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { objectName, type ObjectRecord } from '@/services/db/object/entity';
import { relatedCustomersAllKey, useObject } from '@/services/db/object/queries';
import { objectTypeKeys, type ObjectType } from '@/services/db/object-type/entity';
import { useCanConfigureObjects, useIsRedacted } from '@/services/permissions/hooks';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { SafeNavigate, useSafeNavigate } from '@/services/router/safe-navigate';
import { AddRelationshipsModal } from './components/add-relationships-modal';
import { DeleteObjectsModal } from './components/delete-objects-modal';
import { ObjectDetailProvider, type ObjectDetailContextValue } from './object-detail/context';
import { ObjectDetailHeader } from './object-detail/object-detail-header';
import { ExportObjectModal, ExportObjectProgressModal } from './object-detail/export-object-modal';
import { activeObjectDetailTab } from './object-detail/tabs';
import { ObjectTypeResolver } from './object-type-resolver';

type ObjectDetailResolvedProps = {
  objectType: ObjectType;
  workspaceId: number;
  record: ObjectRecord;
  children: ReactNode;
};

function ObjectDetailResolved({ objectType, workspaceId, record, children }: ObjectDetailResolvedProps) {
  const router = useRouter();
  const location = useLocation();
  const safeNavigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const modals = useModals();
  const { hasPermission } = usePermissions();
  const canConfigureObjects = useCanConfigureObjects();
  const isRedacted = useIsRedacted();
  const environment = useEnvironmentOptional().data?.environment;

  const overviewHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/$objectId',
    params: { workspaceId, slug: objectType.slug, objectId: record.id },
  }).href;
  const typeListHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug',
    params: { workspaceId, slug: objectType.slug },
  }).href;
  const tabParams = { workspaceId, slug: objectType.slug, objectId: record.id };
  const attributesHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/$objectId/attributes',
    params: tabParams,
  }).href;
  const attributesEditHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/$objectId/attributes/edit',
    params: tabParams,
  }).href;
  const relationshipsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/$objectId/relationships',
    params: tabParams,
  }).href;
  const activityHref = router.buildLocation({
    to: '/workspaces/$workspaceId/objects/$slug/$objectId/activity',
    params: tabParams,
  }).href;

  const openAddRelationships = () => {
    const id = modals.open(
      AddRelationshipsModal,
      { objectType, workspaceId, preselectedObject: record, source: 'Object Profile' },
      { shouldCloseOnUnmount: true, withImmediateCallbacks: true },
    );
    void modals.waitForClose(id).then((result) => {
      if (result.reason !== 'confirm') return;
      void queryClient.invalidateQueries({
        queryKey: relatedCustomersAllKey(workspaceId, record.identifiers.cio_object_id),
      });
    });
  };

  const detail: ObjectDetailContextValue = {
    record,
    objectType,
    workspaceId,
    canConfigureObjects,
    canCreateRelationships: hasPermission('journeys/object_relationships/create'),
    // Ember gates Delete on canConfigureObjects (any of create/edit/delete);
    // tightened to the delete permission to match the object list's bulk delete.
    canDelete: hasPermission('journeys/objects/delete'),
    canExport: hasPermission('journeys/exports/create'),
    isMultiIdentifier: environment?.identifier_type === IdentifierType.MultiIdentifier,
    shouldRedactCreatedAt: isRedacted && environment?.is_sample_workspace !== true,
    activeTab: activeObjectDetailTab(location.pathname, record.id),
    onAddRelationships: openAddRelationships,
    hrefs: {
      overview: overviewHref,
      tabs: {
        attributes: attributesHref,
        relationships: relationshipsHref,
        activity: activityHref,
      },
      attributesEdit: attributesEditHref,
      typeList: typeListHref,
    },
  };

  useRegisterBreadcrumbs([
    {
      id: `object.${record.id}`,
      text: objectName(record) ?? record.id,
      titleText: `Object ${record.id}`,
      className: 'fs-mask',
      isTruncated: true,
      href: overviewHref,
    },
  ]);

  const openDelete = () => {
    const id = modals.open(
      DeleteObjectsModal,
      {
        objectTypeId: objectType.id,
        objectTypeName: objectType.name,
        count: 1,
        selection: { cioObjectIds: [record.identifiers.cio_object_id] },
      },
      { shouldCloseOnUnmount: true, withImmediateCallbacks: true },
    );
    void modals.waitForClose(id).then((result) => {
      if (result.reason !== 'confirm') return;
      // The type's object count changed; refetch the catalog like Ember's findAll.
      void queryClient.invalidateQueries({ queryKey: objectTypeKeys.all(workspaceId) });
      safeNavigate(typeListHref);
    });
  };

  const openExport = () => {
    const exportData = {
      workspaceId,
      displayName: objectName(record) ?? record.id,
      cioObjectId: record.identifiers.cio_object_id,
    };
    const id = modals.open(ExportObjectModal, exportData, { shouldCloseOnUnmount: true, withImmediateCallbacks: true });
    void modals.waitForClose(id).then((result) => {
      if (result.reason !== 'confirm') return;
      modals.open(ExportObjectProgressModal, exportData, { shouldCloseOnUnmount: true });
    });
  };

  return (
    <ObjectDetailProvider value={detail}>
      <DocumentTitle title={objectName(record) ?? record.id} />
      <Box>
        <ObjectDetailHeader onDelete={openDelete} onExport={openExport} onAddRelationships={openAddRelationships} />
        {children}
      </Box>
    </ObjectDetailProvider>
  );
}

type ObjectDetailContentProps = { children: ReactNode };

function ObjectDetailContent({ children }: ObjectDetailContentProps) {
  return (
    <ObjectTypeResolver route="/workspaces/$workspaceId/objects/$slug/$objectId">
      {(objectType, workspaceId) => (
        <ObjectDetailRecord objectType={objectType} workspaceId={workspaceId}>
          {children}
        </ObjectDetailRecord>
      )}
    </ObjectTypeResolver>
  );
}

type ObjectDetailRecordProps = { objectType: ObjectType; workspaceId: number; children: ReactNode };

function ObjectDetailRecord({ objectType, workspaceId, children }: ObjectDetailRecordProps) {
  const router = useRouter();
  const { objectId } = useParams({ strict: false });
  const { data: record } = useObject({ objectTypeId: objectType.id, identifier: objectId ?? '' });

  // A bookmarked or shared URL may carry the external object_id; normalize it
  // to the internal id so the canonical URL is the one that gets shared on.
  if (objectId !== record.id) {
    const href = router.buildLocation({
      to: '/workspaces/$workspaceId/objects/$slug/$objectId',
      params: { workspaceId, slug: objectType.slug, objectId: record.id },
    }).href;
    return <SafeNavigate to={href} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return (
    <ObjectDetailResolved objectType={objectType} workspaceId={workspaceId} record={record}>
      {children}
    </ObjectDetailResolved>
  );
}

type ObjectDetailPageProps = { children: ReactNode };

export default function ObjectDetailPage({ children }: ObjectDetailPageProps) {
  return (
    <Suspense fallback={<LoadingPage />}>
      <ObjectDetailContent>{children}</ObjectDetailContent>
    </Suspense>
  );
}
